/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AiProvidersPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    AI Providers main page with data grid for managing AI service providers.
----------------------------------------------------------------*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useDataGrid, type DataGridFetchResult } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { deleteAiProviderById, navigateToAiProviderDuplicate } from './aiProvidersActions';
import { useAiProvidersList } from './hooks/useAiProvidersList';
import { useAiProvidersColumns } from './hooks/useAiProvidersColumns';
import { AiProvidersActions } from './components/AiProvidersActions';
import type { AiProvider } from './api/aiProvidersApi';

export default function AiProvidersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<AiProvider | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Hooks
  const { fetchProviders } = useAiProvidersList();
  const { availableColumns, getColumnRenderer } = useAiProvidersColumns();

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchProviders as (params: { page: number; limit: number; sortBy: string; sortOrder: "desc" | "asc"; search?: string; }) => Promise<DataGridFetchResult>,
    initialColumns: availableColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'aiProviders'
  });

  // Handlers
  const handleCreateProvider = useCallback(() => {
    navigate('/admin/ai-integration/providers/new');
  }, [navigate]);

  const handleEditProvider = useCallback((provider: AiProvider) => {
    navigate(`/admin/ai-integration/providers/${provider.uuid ?? provider.id}`);
  }, [navigate]);

  const handleDuplicateProvider = useCallback((provider: AiProvider) => {
    navigateToAiProviderDuplicate(navigate, provider.uuid ?? provider.id);
  }, [navigate]);

  const handleDeleteProvider = useCallback((provider: AiProvider) => {
    setProviderToDelete(provider);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!providerToDelete) return;

    try {
      setIsDeleting(true);
      await deleteAiProviderById(providerToDelete.id);
      setShowDeleteModal(false);
      setProviderToDelete(null);
      showSuccess(t('aiProviders.messages.deleteSuccess'));
      refresh();
    } catch (error) {
      showError(error?.message || t('aiProviders.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [providerToDelete, refresh, showSuccess, showError, t]);

  // Render actions
  const renderActions = useCallback((provider: AiProvider) => (
    <AiProvidersActions
      provider={provider}
      onEdit={handleEditProvider}
      onDuplicate={handleDuplicateProvider}
      onDelete={handleDeleteProvider}
    />
  ), [handleEditProvider, handleDuplicateProvider, handleDeleteProvider]);

  return (
    <ModulePageContainer 
      currentView="list" 
      listView={
        <DataGridSimple
          data={data}
          columns={columns.map(col => ({
            ...col,
            render: getColumnRenderer(col.key)
          }))}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreateProvider}
          onEdit={handleEditProvider}
          onDuplicate={handleDuplicateProvider}
          onDelete={handleDeleteProvider}
          title={t('aiProviders.title')}
          titleIcon={<Bot className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('common.searchPlaceholder')}
          emptyMessage={t('common.noItemsFound')}
          deleteTitle={t('aiProviders.actions.delete')}
          deleteMessage={(item) => {
            if (!item || !item.name) {
              return t('aiProviders.messages.deleteConfirm', { name: '' });
            }
            return t('aiProviders.messages.deleteConfirm', { name: item.name });
          }}
          renderActions={renderActions}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('aiProviders.actions.delete')}
        message={providerToDelete ? t('aiProviders.messages.deleteConfirm', { 
          name: providerToDelete.name 
        }) : ''}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />
    </ModulePageContainer>
  );
}
